'use client';
import type * as React from 'react';
import { useStore } from '@base-ui/utils/store';
import type {
  SchedulerEventCreationConfig,
  SchedulerOccurrencePlaceholderCreation,
} from '../../models';
import { useSchedulerStoreContext } from '../../use-scheduler-store-context';
import { schedulerEventSelectors } from '../../scheduler-selectors';

interface GetCreationPlaceholderParams {
  event: React.MouseEvent<HTMLDivElement>;
  creationConfig: SchedulerEventCreationConfig;
}

export type CreationPlaceholderFields = Omit<SchedulerOccurrencePlaceholderCreation, 'type'>;

export function useEventCreation(
  getCreationPlaceholder: (params: GetCreationPlaceholderParams) => CreationPlaceholderFields,
): Record<string, React.EventHandler<React.MouseEvent<HTMLDivElement>>> {
  const store = useSchedulerStoreContext();
  const creationConfig = useStore(store, schedulerEventSelectors.creationConfig);

  if (creationConfig === false) {
    return {};
  }

  const handler = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    // Don't create events when clicking on existing events
    if (target !== event.currentTarget && target.closest('button, [role="button"]')) {
      return;
    }
    store.setOccurrencePlaceholder(
      {
        type: 'creation',
        ...getCreationPlaceholder({ event, creationConfig }),
      },
      event.nativeEvent,
    );
  };

  if (creationConfig.interaction === 'double-click') {
    return { onDoubleClick: handler };
  }

  return { onClick: handler };
}
